<template>
  <div id="tabs">

    <ul id="tabs-bar">
      <li
        v-for="tab in tabs"
        :class="{ 'current': tab.id == currentTab }"
        :key="tab.id"
      >
        <button @click="$emit('updateCurrentTab', tab.id)">
          {{ strings.Tabs[tab.name] }}
        </button>
      </li>
    </ul>

    <div id="tabs-container">

      <div id="editor" v-show="isCurrentTab('editor')">
        <div class="vertical-pane">
          <Editor
            class="grow"
            ref="y86Editor"
            :scopedStrings="strings.Editor"
            mode="y86"
            :compiler="kernelController.getYas()"
          />
          <div class="horizontal-pane basis">
            <CpuState
              ref="cpuState_editor"
              :scopedStrings="strings.CpuState"
              :initialValue="this.simulatorController.getCPUStateView()"
            />
            <div class="vertical-pane">
              <Registers
                ref="registers_editor"
                :scopedStrings="strings.Registers"
                :initialValue="this.simulatorController.getRegistersView()"
              />
              <div class="horizontal-pane no-stretch">
                <div class="vertical-pane flags-status">
                  <Flags
                    ref="flags_editor"
                    :scopedStrings="strings.Flags"
                    :initialValue="this.simulatorController.getFlagsView()"
                  />
                  <Status
                    ref="status_editor"
                    :scopedStrings="strings.Status"
                    :initialValue="this.simulatorController.getStatusView()"
                  />
                </div>
                <Performance
                  ref="performance_editor"
                  :scopedStrings="strings.Performance"
                  :initialValue="this.simulatorController.getPerformanceView()"
                  v-show="enablePerformanceComponent"
                />
              </div>
            </div>
          </div>
          <Memory
            class="fixed"
            ref="memory_editor"
            :scopedStrings="strings.Memory"
            :initialValue="this.simulatorController.getMemoryView()"
          />
        </div>
      </div>

      <div id="execution" v-show="isCurrentTab('execution')">
        <div class="vertical-pane">
          <ObjectCode
            class="grow"
            ref="objectCode"
            :scopedStrings="strings.ObjectCode"
          />
          <div class="horizontal-pane basis">
            <CpuState
              ref="cpuState_exec"
              :scopedStrings="strings.CpuState"
              :initialValue="this.simulatorController.getCPUStateView()"
            />
            <div class="vertical-pane">
              <Registers
                ref="registers_exec"
                :scopedStrings="strings.Registers"
                :initialValue="this.simulatorController.getRegistersView()"
              />
              <div class="horizontal-pane no-stretch">
                <div class="vertical-pane flags-status">
                  <Flags
                    ref="flags_exec"
                    :scopedStrings="strings.Flags"
                    :initialValue="this.simulatorController.getFlagsView()"
                  />
                  <Status
                    ref="status_exec"
                    :scopedStrings="strings.Status"
                    :initialValue="this.simulatorController.getStatusView()"
                  />
                </div>
                <Performance
                  ref="performance_exec"
                  :scopedStrings="strings.Performance"
                  :initialValue="this.simulatorController.getPerformanceView()"
                  v-show="enablePerformanceComponent"
                />
              </div>
            </div>
          </div>
          <Memory
            class="fixed"
            ref="memory_exec"
            :scopedStrings="strings.Memory"
            :initialValue="this.simulatorController.getMemoryView()"
          />
        </div>
      </div>

      <div id="instructionSet" v-show="isCurrentTab('instructionSet')">
        <InstructionSet
          ref="InstructionSet"
          :scopedStrings="strings.InstructionSet"
          :kernelController="this.kernelController"
          @instructionSetUpdated="this.setEditorInstructionRecognitionRegex"
          @showPromptDialog="this.showPromptDialog"
        />
      </div>

      <div id="hcl" v-show="isCurrentTab('hcl')">
        <div class="vertical-pane">
          <Editor
            ref="hclEditor"
            :scopedStrings="strings.Editor"
            mode="hcl"
            :compiler="hclController"
          />
        </div>
      </div>

    </div>

  </div>
</template>

<script>
import CpuState from './CpuState.vue'
import Editor from './Editor.vue'
import Flags from './Flags.vue'
import InstructionSet from './InstructionSet.vue'
import Memory from './Memory.vue'
import ObjectCode from './ObjectCode.vue'
import Performance from './Performance.vue'
import Registers from './Registers.vue'
import Status from './Status.vue'

export default {
  name:"Tabs",
  props: {
    strings: {
      type: Object,
      required: true
    },
    currentTab: {
      type: Number,
      required: true
    },
    tabs: {
      type: Array,
      required: true
    },
    kernelController: {
      type: Object,
      required: true
    },
    hclController: {
      type: Object,
      required: true
    },
    simulatorController: {
      type: Object,
      required: true
    }
  },
  components: {
    CpuState,
    Editor,
    Flags,
    InstructionSet,
    Memory,
    ObjectCode,
    Performance,
    Registers,
    Status
  },
  computed: {
    enablePerformanceComponent() {
      return this.simulatorController.getPerformanceView().length != 0
    }
  },
  methods: {
    isCurrentTab(tabName) {
      return tabName.localeCompare(this.tabs[this.currentTab].name) ? false : true
    },
    changeDefaultInstructionSet(newKernelDefaultInstructionSet) {
      this.$refs.InstructionSet.changeDefault(newKernelDefaultInstructionSet)
    },
    getInstructionSet() {
      return this.$refs.InstructionSet.getInstructionSet()
    },
    setInstructionSet(newInstructionSet) {
      this.$refs.InstructionSet.setInstructionSet(newInstructionSet)
    },
    getY86Code() {
      return this.$refs.y86Editor.getCode()
    },
    setY86Code(code) {
      this.$refs.y86Editor.setCode(code)
    },
    getHclCode() {
      return this.$refs.hclEditor.getCode()
    },
    setHclCode(code) {
      this.$refs.hclEditor.setCode(code)
    },
    initObjCode(compilationResult, highlightRules) {
      this.$refs.objectCode.init(compilationResult, highlightRules)
    },
    getCompilationResult() {
      return this.$refs.objectCode.getCompilationResult()
    },
    resetObjCode() {
      this.$refs.objectCode.reset()
      this.simulatorController.reset()
    },
    setEditorInstructionRecognitionRegex(instructionNames) {
      this.$refs.y86Editor.setInstructionRecognitionRegex(instructionNames)
    },
    showPromptDialog(type, message, detail) {
      this.$emit('showPromptDialog', type, message, detail)
    },
    simulationCanRun() {
      return !this.$refs.objectCode.isEmpty() && this.simulatorController.getStatusView()[0].value === "AOK"
    },
    tightenInterface(tightened) {
      this.$refs.cpuState_editor.setTightenedInterface(tightened)
      this.$refs.cpuState_exec.setTightenedInterface(tightened)
    },
    updateEditors() {
      if (this.isCurrentTab("editor")) {
        this.$refs.y86Editor.$nextTick(function () {
          this.updateEditor()
        })
      }
      else if(this.isCurrentTab("hcl")) {
        this.$refs.hclEditor.$nextTick(function () {
          this.updateEditor()
        })
      }
    },
    /**
     * Triggers the computation of the height of a memory word by the next displayed Memory component.
     * The computation needs to begin after the component's DOM is rendered in order to get the appropriate height.
     * Reminder : there's one Memory component for the "editor" tab and another for the "execution" tab.
     */
    updateStackPointersPosition() {
      if (this.isCurrentTab("editor")) {
        this.$refs.memory_editor.$nextTick(function () {  // the component's DOM is now rendered
          this.updateMemoryWordHeight()                   // this now refers to the targeted component
        })
      }
      else if (this.isCurrentTab("execution")) {
        this.$refs.memory_exec.$nextTick(function () {
          this.updateMemoryWordHeight()
        })
      }
    },
    updateObjCodeHighlightRules(newRules) {
      this.$refs.objectCode.updateHighlightRules(newRules)
    },
    updateMemory(newValue) {
      this.$refs.memory_editor.update(newValue)
      this.$refs.memory_exec.update(newValue)
    },
    updateCpuState(newValue) {
      this.$refs.cpuState_editor.update(newValue)
      this.$refs.cpuState_exec.update(newValue)
    },
    updateRegisters(newValue) {
      this.$refs.registers_editor.update(newValue)
      this.$refs.registers_exec.update(newValue)
    },
    updateFlags(newValue) {
      this.$refs.flags_editor.update(newValue)
      this.$refs.flags_exec.update(newValue)
    },
    updateStatus(newValue) {
      this.$refs.status_editor.update(newValue)
      this.$refs.status_exec.update(newValue)
    },
    updatePerformance(newValue) {
      this.$refs.performance_editor.update(newValue)
      this.$refs.performance_exec.update(newValue)
    }
  },
  watch: {
    /**
     * Triggered on tab update.
     */
    currentTab(newTab, oldTab){
      this.updateStackPointersPosition()
      this.updateEditors()
    }
  }
}
</script>

<style>
  #tabs {
    display: flex;
    flex-direction: column;
    height: 100%;
  }

  #tabs-bar {
    display: flex;
    margin: 0;
    padding: 0;
  }

  #tabs-bar > li {
    flex-grow: 1;
    list-style-type: none;
  }

  #tabs-bar > li > button {
    width: 100%;
    font-weight: 900;
    padding: 10px 0;
    background-color: var(--Tabs-button-background);
    color: var(--Tabs-button-foreground);
    font-size: 16px;
    font-variant-caps: small-caps;
    transition-duration: .4s;
  }

  #tabs-bar > li > button:hover {
    background-color: var(--Tabs-hover-button-background);
    color: var(--Tabs-hover-button-foreground);
  }

  #tabs-bar > li.current > button {
    background-color: var(--Tabs-current-button-background);
    color: var(--Tabs-current-button-foreground);
  }

  #tabs-container {
    height: 100%;
  }

  #tabs-container > div {
    flex-grow: 1;
    height: 100%;
  }

  .horizontal-pane,
  .vertical-pane {
    height: 100%;
    display: flex;
  }

  .horizontal-pane {
    flex-direction: column;
  }

  .vertical-pane {
    flex-direction: row;
  }

  .horizontal-pane > *,
  .vertical-pane > * {
    flex-grow: 1;
  }

  .grow {
    flex: 10 0;
  }

  .basis {
    flex-basis: 30%;
  }

  .fixed {
    flex: 0 0;
  }

  .no-stretch {
    height: max-content;
  }

  .component {
    display: flex;
    flex-direction: column;
  }

  .component h1 {
    margin: 0;
    padding: 12px 0 8px 16px;
    background-color: var(--component-header-background);
    color: var(--component-header-foreground);
    font-size: 14px;
    text-transform: uppercase;
  }

  .component > div {
    flex-grow: 1;
  }

  .component h2 {
    margin: 0 4px 0 1px;
    padding: 10px 0 6px 14px;
    background-color: var(--component-subheader-background);
    border-bottom: 1px solid var(--component-subheader-border);
    color: var(--component-subheader-foreground);
    font-size: 12px;
    letter-spacing: 1px;
    text-transform: uppercase;
  }

  .component .label,
  .component .value {
    padding: 5px 6px 4px 6px;
    font-family: "Monospaced Font";
  }

  .component .label {
    margin-left: 8px;
    background-color: var(--component-label-background);
    color: var(--component-label-foreground);
    font-weight: 900;
  }

  .component table {
    border-collapse: collapse;
  }
</style>
